import { useState, useRef } from "react";
import { MapPin, Phone, Mail, Clock, MessageCircle, Check, Instagram, Upload, X, FileText } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { Calendar } from "@/components/ui/calendar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { format } from "date-fns";
import { fr } from "date-fns/locale";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIME_SLOTS = [
  "10:00",
  "10:30",
  "11:00",
  "11:30",
  "12:00",
  "12:30",
  "13:00",
  "13:30",
  "14:00",
  "14:30",
  "15:00",
  "15:30",
  "16:00",
  "16:30",
  "17:00",
  "17:30",
  "18:00",
  "18:30",
];

export default function ContactPage() {
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    date: null,
    time: "",
    message: "",
  });
  const [files, setFiles] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const fileInputRef = useRef(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleDateSelect = (date) => {
    setFormData((prev) => ({ ...prev, date }));
  };

  const handleTimeChange = (value) => {
    setFormData((prev) => ({ ...prev, time: value }));
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    setFiles((prev) => [...prev, ...selectedFiles]);
  };

  const removeFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.date || !formData.time) {
      toast.error("Veuillez sélectionner une date et une heure");
      return;
    }

    setIsSubmitting(true);

    try {
      // Create FormData for file upload
      const submitData = new FormData();
      submitData.append("first_name", formData.first_name);
      submitData.append("last_name", formData.last_name);
      submitData.append("email", formData.email);
      submitData.append("phone", formData.phone);
      submitData.append("date", format(formData.date, "yyyy-MM-dd"));
      submitData.append("time", formData.time);
      submitData.append("message", formData.message || "");
      
      // Append files
      files.forEach((file) => {
        submitData.append("files", file);
      });

      await axios.post(`${API}/appointments-with-files`, submitData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      setIsSuccess(true);
      toast.success("Votre rendez-vous a été enregistré !");
    } catch (error) {
      toast.error("Une erreur est survenue. Veuillez réessayer.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Disable only past dates (all days available)
  const disabledDays = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };

  return (
    <div data-testid="contact-page" className="min-h-screen bg-[#F9F9F7]">
      {/* Hero */}
      <section className="pt-32 pb-16 bg-[#F9F9F7]">
        <div className="container-premium">
          <div className="max-w-3xl">
            <p className="caption text-[#D4AF37] mb-6">Rendez-vous</p>
            <h1 className="font-serif text-5xl md:text-7xl font-medium tracking-tight leading-[1.1] mb-8 text-[#1A1A1A]">
              Contactez-nous
            </h1>
            <p className="text-xl font-light text-[#666660] leading-relaxed">
              Prenez rendez-vous pour une consultation personnalisée 
              ou contactez-nous directement pour toute question.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="section-padding bg-[#F9F9F7]">
        <div className="container-premium">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Info */}
            <div>
              <h2 className="font-serif text-3xl mb-8 text-[#1A1A1A]">
                Informations
              </h2>

              <div className="space-y-8">
                {/* Address */}
                <div className="flex gap-4">
                  <div className="w-12 h-12 flex-shrink-0 flex items-center justify-center border border-[#D4AF37]">
                    <MapPin size={20} className="text-[#D4AF37]" strokeWidth={1} />
                  </div>
                  <div>
                    <h3 className="font-serif text-lg mb-1 text-[#1A1A1A]">
                      Adresse
                    </h3>
                    <p className="text-[#666660] font-light">
                      1 Rue de la Gare
                      <br />
                      77360 Vaires-sur-Marne
                    </p>
                    <p className="text-[#666660] font-light text-sm mt-2">
                      En face de la gare RER P • Parking gratuit 1h30
                    </p>
                  </div>
                </div>

                {/* Phone */}
                <div className="flex gap-4">
                  <div className="w-12 h-12 flex-shrink-0 flex items-center justify-center border border-[#D4AF37]">
                    <Phone size={20} className="text-[#D4AF37]" strokeWidth={1} />
                  </div>
                  <div>
                    <h3 className="font-serif text-lg mb-1 text-[#1A1A1A]">
                      Téléphone
                    </h3>
                    <a
                      href="tel:0182350274"
                      className="text-[#666660] hover:text-[#D4AF37] transition-colors duration-300 font-light block"
                    >
                      01 82 35 02 74
                    </a>
                  </div>
                </div>

                {/* WhatsApp */}
                <div className="flex gap-4">
                  <div className="w-12 h-12 flex-shrink-0 flex items-center justify-center border border-[#25D366] bg-[#25D366]">
                    <MessageCircle size={20} className="text-white" strokeWidth={1} />
                  </div>
                  <div>
                    <h3 className="font-serif text-lg mb-1 text-[#1A1A1A]">
                      WhatsApp
                    </h3>
                    <a
                      href="https://wa.me/33781477380"
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="contact-whatsapp-link"
                      className="text-[#666660] hover:text-[#25D366] transition-colors duration-300 font-light block"
                    >
                      07 81 47 73 80
                    </a>
                  </div>
                </div>

                {/* Email */}
                <div className="flex gap-4">
                  <div className="w-12 h-12 flex-shrink-0 flex items-center justify-center border border-[#D4AF37]">
                    <Mail size={20} className="text-[#D4AF37]" strokeWidth={1} />
                  </div>
                  <div>
                    <h3 className="font-serif text-lg mb-1 text-[#1A1A1A]">
                      Email
                    </h3>
                    <a
                      href="mailto:opt.vaires@gmail.com"
                      className="text-[#666660] hover:text-[#D4AF37] transition-colors duration-300 font-light block"
                    >
                      opt.vaires@gmail.com
                    </a>
                  </div>
                </div>

                {/* Instagram */}
                <div className="flex gap-4">
                  <div className="w-12 h-12 flex-shrink-0 flex items-center justify-center border border-[#E1306C] bg-gradient-to-br from-[#833AB4] via-[#E1306C] to-[#F77737]">
                    <Instagram size={20} className="text-white" strokeWidth={1} />
                  </div>
                  <div>
                    <h3 className="font-serif text-lg mb-1 text-[#1A1A1A]">
                      Instagram
                    </h3>
                    <a
                      href="https://instagram.com/opticien_vaires"
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="contact-instagram-link"
                      className="text-[#666660] hover:text-[#E1306C] transition-colors duration-300 font-light block"
                    >
                      @opticien_vaires
                    </a>
                  </div>
                </div>

                {/* Hours */}
                <div className="flex gap-4">
                  <div className="w-12 h-12 flex-shrink-0 flex items-center justify-center border border-[#D4AF37]">
                    <Clock size={20} className="text-[#D4AF37]" strokeWidth={1} />
                  </div>
                  <div>
                    <h3 className="font-serif text-lg mb-1 text-[#1A1A1A]">
                      Horaires
                    </h3>
                    <p className="text-[#666660] font-light">
                      Tous les jours : 10h - 19h
                    </p>
                  </div>
                </div>
              </div>

              {/* Map placeholder */}
              <div className="mt-12 h-[300px] bg-[#F0F0EB] flex items-center justify-center border border-[#E5E5E0]">
                <iframe
                  title="Localisation Optique Vaires"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2623.8789071458726!2d2.6330!3d48.8730!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47e60d0f0e36a6e9%3A0x0!2s1%20Rue%20de%20la%20Gare%2C%2077360%20Vaires-sur-Marne!5e0!3m2!1sfr!2sfr!4v1699999999999!5m2!1sfr!2sfr"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </div>

            {/* Appointment Form */}
            <div>
              <h2 className="font-serif text-3xl mb-8 text-[#1A1A1A]">
                Prendre Rendez-vous
              </h2>

              {isSuccess ? (
                <div
                  data-testid="appointment-success"
                  className="p-12 border border-[#D4AF37] bg-[#F0F0EB] text-center"
                >
                  <div className="w-16 h-16 mx-auto flex items-center justify-center border-2 border-[#D4AF37] rounded-full mb-6">
                    <Check size={32} className="text-[#D4AF37]" />
                  </div>
                  <h3 className="font-serif text-2xl mb-4 text-[#1A1A1A]">
                    Rendez-vous confirmé
                  </h3>
                  <p className="text-[#666660] font-light mb-6">
                    Nous avons bien reçu votre demande de rendez-vous.
                    <br />
                    Nous vous contacterons rapidement pour confirmation.
                  </p>
                  <button
                    onClick={() => {
                      setIsSuccess(false);
                      setFormData({
                        first_name: "",
                        last_name: "",
                        email: "",
                        phone: "",
                        date: null,
                        time: "",
                        message: "",
                      });
                      setFiles([]);
                    }}
                    className="btn-secondary"
                  >
                    Nouveau rendez-vous
                  </button>
                </div>
              ) : (
                <form
                  onSubmit={handleSubmit}
                  data-testid="appointment-form"
                  className="space-y-6"
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="form-group">
                      <label className="form-label">Prénom *</label>
                      <input
                        type="text"
                        name="first_name"
                        value={formData.first_name}
                        onChange={handleChange}
                        required
                        data-testid="input-first-name"
                        className="input-premium"
                        placeholder="Votre prénom"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Nom *</label>
                      <input
                        type="text"
                        name="last_name"
                        value={formData.last_name}
                        onChange={handleChange}
                        required
                        data-testid="input-last-name"
                        className="input-premium"
                        placeholder="Votre nom"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="form-group">
                      <label className="form-label">Email *</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        data-testid="input-email"
                        className="input-premium"
                        placeholder="votre@email.com"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Téléphone *</label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        data-testid="input-phone"
                        className="input-premium"
                        placeholder="06 XX XX XX XX"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="form-group">
                      <label className="form-label">Date *</label>
                      <div className="border border-[#E5E5E0] p-4 bg-white">
                        <Calendar
                          mode="single"
                          selected={formData.date}
                          onSelect={handleDateSelect}
                          disabled={disabledDays}
                          locale={fr}
                          data-testid="calendar"
                          className="mx-auto"
                        />
                      </div>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Heure *</label>
                      <Select
                        onValueChange={handleTimeChange}
                        value={formData.time}
                      >
                        <SelectTrigger
                          data-testid="select-time"
                          className="w-full bg-transparent border-0 border-b border-[#1A1A1A]/20 rounded-none px-0 py-4 focus:ring-0 focus:border-[#1A1A1A]"
                        >
                          <SelectValue placeholder="Sélectionnez une heure" />
                        </SelectTrigger>
                        <SelectContent>
                          {TIME_SLOTS.map((time) => (
                            <SelectItem key={time} value={time}>
                              {time}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {formData.date && (
                        <p className="text-[#666660] text-sm mt-2">
                          Date sélectionnée :{" "}
                          {format(formData.date, "EEEE d MMMM yyyy", { locale: fr })}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Message (optionnel)</label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows={3}
                      data-testid="input-message"
                      className="input-premium resize-none"
                      placeholder="Des précisions sur votre demande..."
                    />
                  </div>

                  {/* File Upload */}
                  <div className="form-group">
                    <label className="form-label">Documents (optionnel)</label>
                    <p className="text-[#666660] text-sm mb-3">
                      Pour faciliter votre visite, vous pouvez joindre votre ordonnance et/ou carte mutuelle
                    </p>
                    
                    <div 
                      onClick={() => fileInputRef.current?.click()}
                      className="border-2 border-dashed border-[#E5E5E0] hover:border-[#D4AF37] transition-colors duration-300 p-6 text-center cursor-pointer bg-white"
                    >
                      <Upload size={24} className="mx-auto text-[#D4AF37] mb-2" />
                      <p className="text-[#666660] text-sm">
                        Cliquez pour ajouter des fichiers
                      </p>
                      <p className="text-[#999999] text-xs mt-1">
                        PDF, JPG, PNG (max 5 Mo par fichier)
                      </p>
                    </div>
                    
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={handleFileChange}
                      className="hidden"
                      data-testid="file-input"
                    />

                    {/* File List */}
                    {files.length > 0 && (
                      <div className="mt-4 space-y-2">
                        {files.map((file, fileIdx) => (
                          <div 
                            key={`file-${file.name}-${fileIdx}`}
                            className="flex items-center justify-between bg-[#F0F0EB] px-4 py-2"
                          >
                            <div className="flex items-center gap-2">
                              <FileText size={16} className="text-[#D4AF37]" />
                              <span className="text-sm text-[#666660] truncate max-w-[200px]">
                                {file.name}
                              </span>
                            </div>
                            <button
                              type="button"
                              onClick={() => removeFile(fileIdx)}
                              className="text-[#999999] hover:text-red-500 transition-colors"
                            >
                              <X size={16} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    data-testid="submit-appointment"
                    className="btn-primary w-full md:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? "Envoi en cours..." : "Confirmer le rendez-vous"}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
